import { SEO } from "@/components/seo";
import { siteConfig } from "@/config/website.config";
import { broker } from "@/config/broker.config";

export function TermsConditionsPage() {
  const lastUpdated = "2 January 2026";

  return (
    <>
      <SEO
        title="Terms & Conditions"
        description="Terms and conditions for using Path Financial Solutions mortgage broking services."
        url="/terms-conditions"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <p className="section-label mb-4">Legal</p>
          <h1 className="font-serif text-4xl md:text-5xl mb-4">Terms & Conditions</h1>
          <p className="text-muted-foreground">Last updated: {lastUpdated}</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">1. Introduction</h2>
              <p className="text-muted-foreground mb-4">
                In these Terms and Conditions of Use, the expressions we, us and our includes
                Australian Finance Group Ltd ABN 11 066 385 822 (AFG) Australian Credit Licence 389087,
                Path Financial Solutions ABN 97193515627 and any related entity.
              </p>
              <p className="text-muted-foreground">
                By engaging our services or using our website, you agree to be bound by these Terms.
                If you do not agree, please do not use our services.
              </p>
            </div>

            {/* Our Services */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">2. Our Services</h2>
              <p className="text-muted-foreground mb-4">
                We provide mortgage broking services, which include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Assessing your financial situation and borrowing capacity</li>
                <li>Researching and comparing loan products from our panel of lenders</li>
                <li>Providing recommendations on suitable loan products</li>
                <li>Preparing and submitting loan applications on your behalf</li>
                <li>Liaising with lenders throughout the application process</li>
                <li>Assisting with loan settlement</li>
                <li>Providing ongoing loan review services</li>
              </ul>
              <p className="text-muted-foreground">
                We act as an intermediary between you and lenders. We do not lend money directly
                and cannot guarantee loan approval.
              </p>
            </div>

            {/* Regulatory Framework */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">3. Regulatory Framework</h2>
              <p className="text-muted-foreground mb-4">
                Our services are regulated under Australian law, including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>National Consumer Credit Protection Act 2009 (Cth)</li>
                <li>National Credit Code</li>
                <li>Australian Securities and Investments Commission Act 2001 (Cth)</li>
                <li>Anti-Money Laundering and Counter-Terrorism Financing Act 2006 (Cth)</li>
                <li>Privacy Act 1988 (Cth)</li>
              </ul>
              <p className="text-muted-foreground">
                We are required to act in your best interests when providing credit assistance
                and to ensure any credit contract we recommend is not unsuitable for you.
              </p>
            </div>

            {/* Your Obligations */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">4. Your Obligations</h2>
              <p className="text-muted-foreground mb-4">When using our services, you agree to:</p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Provide accurate, complete, and current information</li>
                <li>Notify us promptly of any changes to your circumstances</li>
                <li>Not provide false or misleading information</li>
                <li>Read and understand all loan documents before signing</li>
                <li>Meet your obligations under any loan contract you enter</li>
                <li>Pay any fees or charges as agreed</li>
                <li>Respond to our requests for information in a timely manner</li>
              </ul>
              <p className="text-muted-foreground">
                Providing false or misleading information may constitute fraud and could result
                in loan rejection, legal action, or criminal charges.
              </p>
            </div>

            {/* Fees and Commissions */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">5. Fees and Commissions</h2>
              <p className="text-muted-foreground mb-4">
                <strong>Our service is free to you.</strong> We do not charge you fees for our
                mortgage broking services.
              </p>
              <p className="text-muted-foreground mb-4">
                We receive commissions from lenders when your loan settles. These typically include:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li><strong>Upfront commission:</strong> A percentage of the loan amount paid at settlement</li>
                <li><strong>Trail commission:</strong> An ongoing percentage of the loan balance paid monthly</li>
              </ul>
              <p className="text-muted-foreground mb-4">
                Commission rates vary between lenders. We will disclose the estimated commission
                we expect to receive in writing before you proceed with a loan application.
              </p>
              <p className="text-muted-foreground">
                Our commission arrangements do not affect the interest rate or fees you pay on
                your loan. The lender sets these independently.
              </p>
            </div>

            {/* Lender Panel */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">6. Lender Panel</h2>
              <p className="text-muted-foreground mb-4">
                We have access to a panel of over 70 lenders, including major banks, regional
                banks, credit unions, and specialist lenders. Our lender panel is curated by our
                aggregator, {siteConfig.business.aclProvider}.
              </p>
              <p className="text-muted-foreground mb-4">
                We recommend products from this panel that we believe are suitable for your
                circumstances. We do not compare every loan product in the market.
              </p>
              <p className="text-muted-foreground">
                A full list of lenders on our panel is available on request.
              </p>
            </div>

            {/* No Guarantee */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">7. No Guarantee of Approval</h2>
              <p className="text-muted-foreground mb-4">
                We cannot guarantee that any loan application will be approved. Loan approval
                is at the sole discretion of the lender and depends on factors including:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Your creditworthiness and credit history</li>
                <li>Your income and employment stability</li>
                <li>The property valuation</li>
                <li>The lender's lending criteria at the time of application</li>
                <li>Regulatory requirements</li>
              </ul>
              <p className="text-muted-foreground">
                Pre-approvals are subject to conditions and do not guarantee final approval.
              </p>
            </div>

            {/* Conflicts of Interest */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">8. Conflicts of Interest</h2>
              <p className="text-muted-foreground mb-4">
                We are required to identify and manage conflicts of interest. Potential conflicts
                may arise because:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>We receive commissions from lenders</li>
                <li>Commission rates vary between lenders</li>
                <li>Some lenders may offer volume bonuses or incentives</li>
              </ul>
              <p className="text-muted-foreground">
                We manage these conflicts by always prioritising your best interests, disclosing
                commissions, and maintaining a diverse lender panel. Our recommendations are
                based on product suitability, not commission rates.
              </p>
            </div>

            {/* Website Use */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">9. Website Use</h2>
              <p className="text-muted-foreground mb-4">
                Our website is provided for informational purposes. When using our website, you agree not to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>Use the website for any unlawful purpose</li>
                <li>Attempt to gain unauthorised access to our systems</li>
                <li>Interfere with the website's operation</li>
                <li>Copy, reproduce, or distribute website content without permission</li>
                <li>Submit false information through forms or calculators</li>
              </ul>
              <p className="text-muted-foreground">
                Calculator results are estimates only and should not be relied upon as financial advice.
              </p>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">10. Intellectual Property</h2>
              <p className="text-muted-foreground">
                All content on our website, including text, images, logos, and design elements,
                is owned by us or our licensors and is protected by copyright and trademark laws.
                You may not reproduce, distribute, or create derivative works without our written permission.
              </p>
            </div>

            {/* Limitation of Liability */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">11. Limitation of Liability</h2>
              <p className="text-muted-foreground mb-4">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground mb-4 space-y-1">
                <li>We provide our services "as is" without warranties of any kind</li>
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our liability is limited to the fees paid by you (if any)</li>
                <li>We are not responsible for lender decisions or delays</li>
                <li>We are not liable for losses arising from reliance on website information</li>
              </ul>
              <p className="text-muted-foreground">
                Nothing in these Terms excludes or limits liability that cannot be excluded
                under Australian Consumer Law.
              </p>
            </div>

            {/* Indemnity */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">12. Indemnity</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold us harmless from any claims, losses, or expenses
                arising from your breach of these Terms, provision of false information, or
                failure to meet your obligations under any loan contract.
              </p>
            </div>

            {/* Dispute Resolution */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">13. Dispute Resolution</h2>
              <p className="text-muted-foreground mb-4">
                If you have a complaint about our services:
              </p>
              <ol className="list-decimal pl-6 text-muted-foreground mb-4 space-y-2">
                <li><strong>Contact us first:</strong> We will try to resolve your complaint within 30 days.</li>
                <li><strong>Internal Dispute Resolution:</strong> If unresolved, we will escalate to our internal dispute resolution process.</li>
                <li><strong>External Dispute Resolution:</strong> If still unresolved, you may lodge a complaint with the Australian Financial Complaints Authority (AFCA).</li>
              </ol>
              <div className="bg-muted rounded-xl p-6 mt-4">
                <p className="font-semibold mb-2">Australian Financial Complaints Authority</p>
                <ul className="list-none text-muted-foreground space-y-1">
                  <li>Website: <a href="https://www.afca.org.au" className="text-primary hover:underline">www.afca.org.au</a></li>
                  <li>Phone: 1800 931 678</li>
                  <li>Email: info@afca.org.au</li>
                </ul>
              </div>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">14. Governing Law</h2>
              <p className="text-muted-foreground">
                These Terms are governed by the laws of New South Wales, Australia. You submit
                to the non-exclusive jurisdiction of the courts of New South Wales.
              </p>
            </div>

            {/* Amendments */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">15. Amendments</h2>
              <p className="text-muted-foreground">
                We may amend these Terms at any time by posting the updated version on our website.
                Continued use of our services after changes are posted constitutes acceptance of
                the amended Terms.
              </p>
            </div>

            {/* Severability */}
            <div className="mb-12">
              <h2 className="font-serif text-2xl mb-4">16. Severability</h2>
              <p className="text-muted-foreground">
                If any provision of these Terms is found to be invalid or unenforceable, the
                remaining provisions will continue in full force and effect.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-cream rounded-2xl p-8">
              <h2 className="font-serif text-2xl mb-4">Contact Us</h2>
              <p className="text-muted-foreground mb-4">
                For questions about these Terms, please contact:
              </p>
              <ul className="list-none text-muted-foreground space-y-2">
                <li><strong>Contact:</strong> {broker.name}</li>
                <li><strong>Business:</strong> {siteConfig.appName}</li>
                <li><strong>Email:</strong> <a href={`mailto:${broker.email}`} className="text-primary hover:underline">{broker.email}</a></li>
                <li><strong>Phone:</strong> <a href={`tel:${broker.phone.replace(/\s/g, "")}`} className="text-primary hover:underline">{broker.phone}</a></li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
